import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import { useState, useMemo, useCallback } from 'react';
import { getCsrfToken, postJSON, getCSRFHeaders } from '@folklore/fetch';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject'; // prettier-ignore

var getFieldsPropsFromFields = function getFieldsPropsFromFields(fields, _ref) {
  var value = _ref.value,
      errors = _ref.errors,
      _onChange = _ref.onChange,
      props = _objectWithoutProperties(_ref, ["value", "errors", "onChange"]);

  return fields.reduce(function (allFields, field) {
    var _ref2 = isObject(field) ? field : {},
        _ref2$name = _ref2.name,
        name = _ref2$name === void 0 ? isString(field) ? field : null : _ref2$name;

    return _objectSpread({}, allFields, _defineProperty({}, name, _objectSpread({}, isObject(field) ? field : null, {
      name: name,
      value: value !== null ? value[name] || null : null,
      errors: errors !== null ? errors[name] || null : null,
      onChange: function onChange(fieldValue) {
        return _onChange(name, fieldValue);
      }
    }, props)));
  }, {});
};

var useForm = function useForm() {
  var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _opts$fields = opts.fields,
      fields = _opts$fields === void 0 ? [] : _opts$fields,
      _opts$action = opts.action,
      action = _opts$action === void 0 ? null : _opts$action,
      _opts$errors = opts.errors,
      providedErrors = _opts$errors === void 0 ? null : _opts$errors,
      _opts$setErrors = opts.setErrors,
      setProvidedErrors = _opts$setErrors === void 0 ? null : _opts$setErrors,
      _opts$generalError = opts.generalError,
      providedGeneralError = _opts$generalError === void 0 ? null : _opts$generalError,
      _opts$setGeneralError = opts.setGeneralError,
      setProvidedGeneralError = _opts$setGeneralError === void 0 ? null : _opts$setGeneralError,
      _opts$value = opts.value,
      providedValue = _opts$value === void 0 ? null : _opts$value,
      _opts$setValue = opts.setValue,
      setProvidedValue = _opts$setValue === void 0 ? null : _opts$setValue,
      _opts$onComplete = opts.onComplete,
      onComplete = _opts$onComplete === void 0 ? null : _opts$onComplete;

  var _useState = useState(providedValue),
      _useState2 = _slicedToArray(_useState, 2),
      stateValue = _useState2[0],
      setStateValue = _useState2[1];

  var _useState3 = useState(providedErrors),
      _useState4 = _slicedToArray(_useState3, 2),
      stateErrors = _useState4[0],
      setStateErrors = _useState4[1];

  var _useState5 = useState(providedGeneralError),
      _useState6 = _slicedToArray(_useState5, 2),
      stateGeneralError = _useState6[0],
      setStateGeneralError = _useState6[1];

  var _useState7 = useState({
    success: false,
    loading: false,
    error: false
  }),
      _useState8 = _slicedToArray(_useState7, 2),
      requestState = _useState8[0],
      setRequestState = _useState8[1];

  var _useState9 = useState(null),
      _useState10 = _slicedToArray(_useState9, 2),
      response = _useState10[0],
      setResponse = _useState10[1];

  var hasProvidedValue = setProvidedValue !== null;
  var value = hasProvidedValue ? providedValue : stateValue;
  var setValue = hasProvidedValue ? setProvidedValue : setStateValue;
  var hasProvidedErrors = setProvidedErrors !== null;
  var errors = hasProvidedErrors ? providedErrors : stateErrors;
  var setErrors = hasProvidedErrors ? setProvidedErrors : setStateErrors;
  var hasProvidedGeneralError = setProvidedGeneralError !== null;
  var generalError = hasProvidedGeneralError ? providedGeneralError : stateGeneralError;
  var setGeneralError = hasProvidedGeneralError ? setProvidedGeneralError : setStateGeneralError;
  var fieldsKey = [value, errors].concat(fields);
  var onFieldChange = useCallback(function (fieldName, fieldValue) {
    var fieldErrors = errors !== null ? errors[fieldName] || null : null;

    if (fieldErrors !== null) {
      setErrors(_objectSpread({}, errors, _defineProperty({}, fieldName, null)));
    }

    setValue(_objectSpread({}, value, _defineProperty({}, fieldName, fieldValue)));
  }, fieldsKey);
  var fieldsProps = useMemo(function () {
    return getFieldsPropsFromFields(fields, {
      value: value,
      errors: errors,
      onChange: onFieldChange
    });
  }, fieldsKey);
  var csrfToken = useMemo(function () {
    return getCsrfToken();
  }, []);

  var onSubmitError = function onSubmitError(error) {
    setRequestState({
      success: false,
      loading: false,
      error: true
    });

    if (error.name === 'ValidationError') {
      var _error$getResponseDat = error.getResponseData(),
          _error$getResponseDat2 = _error$getResponseDat.errors,
          validationErrors = _error$getResponseDat2 === void 0 ? null : _error$getResponseDat2;

      setErrors(validationErrors);
    } else {
      setGeneralError(error.message);
    }
  };

  var onSubmitSuccess = function onSubmitSuccess(resp) {
    setRequestState({
      success: true,
      loading: false,
      error: false
    });
    setResponse(resp);
    onComplete(resp);
  };

  var submit = useCallback(function () {
    var submitValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : value;
    setRequestState({
      success: false,
      loading: true,
      error: false
    });
    setGeneralError(null);
    setErrors(null);
    postJSON(action, _objectSpread({}, submitValue, {
      _token: csrfToken
    }), {
      credentials: 'include',
      headers: getCSRFHeaders()
    }).then(onSubmitSuccess)["catch"](onSubmitError);
  }, [action, value]);
  var onSubmit = useCallback(function (e) {
    e.preventDefault();
    submit();
  }, [submit]);
  var status = null;

  if (requestState.loading) {
    status = 'loading';
  } else if (requestState.success) {
    status = 'success';
  } else if (requestState.error) {
    status = 'error';
  }

  return _objectSpread({
    value: value,
    setValue: setValue,
    csrfToken: csrfToken,
    submit: submit,
    onSubmit: onSubmit
  }, requestState, {
    status: status,
    response: response,
    fields: fieldsProps,
    errors: errors,
    generalError: generalError
  });
};

export default useForm;